'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { trackCadastroIniciado, trackGoogleOauthClicado } from '@/lib/analytics'
import { isGoogleOAuthEnabled } from '@/lib/auth/google-oauth'

interface GoogleButtonProps {
  mode: 'signin' | 'signup'
  origem?: 'sign-in' | 'sign-up' | 'modal-login' | 'modal-signup'
  /**
   * Path interno (ex: `/calculadoras/margem-de-lucro`) para onde retornar
   * apos o OAuth completar.
   *
   * - `string`: usa esse path explicito.
   * - `'current'`: captura `window.location.pathname` no click (uso
   *   tipico do modal de auth, que pode aparecer em qualquer pagina).
   * - omitido: callback aplica o comportamento padrao (novo user ->
   *   /onboarding, recorrente -> /dashboard).
   */
  from?: string | 'current'
  /** Se false, não renderiza o divisor "ou com email" abaixo do botão. Default true. */
  withDivider?: boolean
}

export function GoogleButton({
  mode,
  origem,
  from,
  withDivider = true,
}: GoogleButtonProps) {
  const [isLoading, setIsLoading] = useState(false)

  if (!isGoogleOAuthEnabled()) return null

  const handleClick = () => {
    setIsLoading(true)
    trackGoogleOauthClicado(origem || (mode === 'signin' ? 'sign-in' : 'sign-up'))
    if (mode === 'signup') trackCadastroIniciado()

    const resolvedFrom =
      from === 'current' ? window.location.pathname : from
    const url = resolvedFrom
      ? `/api/auth/google?from=${encodeURIComponent(resolvedFrom)}`
      : '/api/auth/google'
    window.location.href = url
  }

  const text = mode === 'signin' ? 'Entrar com Google' : 'Cadastrar com Google'
  const dividerLabel =
    mode === 'signin' ? 'ou com email' : 'ou cadastre com email'

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="w-full gap-2"
        onClick={handleClick}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <svg className="h-4 w-4" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        )}
        {text}
      </Button>

      {withDivider && (
        <div className="relative my-6 flex items-center gap-4">
          <span className="flex-1 border-t border-border" />
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {dividerLabel}
          </span>
          <span className="flex-1 border-t border-border" />
        </div>
      )}
    </>
  )
}
